import { Badge, Inline, Stack } from '@reelplicate/ui';

import { AreaChart } from './area-chart';
import { formatDay, formatGain } from './format';

/** Days labelled under the chart: first, two between, last. */
const axisTicks = (days: number) => [
  0,
  Math.round((days - 1) / 3),
  Math.round(((days - 1) * 2) / 3),
  days - 1,
];

/**
 * Figma "Metric · Views" (166:279): the metric's colour dot and name, its
 * change vs the previous range, the gain over the range and its daily chart.
 */
export function MetricCard({
  label,
  color,
  total,
  change,
  daily,
  start,
}: {
  label: string;
  /** A `viz-*` token. */
  color: string;
  /** Gained over the range. */
  total: number;
  /** Percent vs the previous range; null when there's nothing to compare with. */
  change: number | null;
  /** The gain on each day of the range. */
  daily: number[];
  /** The first day of the range. */
  start: Date;
}) {
  const up = (change ?? 0) >= 0;
  const top = Math.max(...daily, 0);
  const bottom = Math.min(...daily, 0);
  // Headroom over the best day, as in Figma (the line peaks at 96 of 100).
  const max = top === bottom ? 1 : top + (top - bottom) * 0.04;
  const day = (i: number) =>
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);

  return (
    <Stack
      as="article"
      gap={3}
      aria-label={label}
      className="bg-bg-surface border-border-subtle min-w-0 rounded-2xl border p-5"
    >
      <Inline gap={2}>
        <span
          aria-hidden
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <h3 className="text-label-md text-text-secondary flex-1">{label}</h3>
        {change !== null && (
          <Badge size="sm" tone={up ? 'success' : 'danger'}>
            <span aria-hidden>{up ? '↑' : '↓'} </span>
            <span className="sr-only">{up ? 'Up' : 'Down'} </span>
            {Math.abs(change)}%
          </Badge>
        )}
      </Inline>
      <p className="text-heading-h2 text-text-primary">{formatGain(total)}</p>
      <AreaChart
        values={daily}
        max={max}
        min={bottom}
        start={start}
        color={color}
        label={`${label}, ${daily.length} days`}
      />
      <Inline aria-hidden align="between" className="text-caption text-text-tertiary">
        {axisTicks(daily.length).map((i) => (
          <span key={i}>{formatDay(day(i))}</span>
        ))}
      </Inline>
    </Stack>
  );
}
